import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate, useParams } from "react-router-dom";
import {
  TextField,
  Button,
  Table,
  TableHead,
  Container,
  AppBar,
  Toolbar,
  TableContainer,
  Typography,
  Paper,
  TableRow,
  TableCell,
  TableBody,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Box,
} from "@mui/material";
import LoadingBar from "react-top-loading-bar";
import SearchIcon from "@mui/icons-material/Search";
import base64 from "base64-js";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const Branch = () => {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const navigate = useNavigate();
  const { encodedFormId } = useParams();
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingBarProgress, setLoadingBarProgress] = useState(0);
  const [canEdit, setCanEdit] = useState(false);
  const [authorization, setAuthorization] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const decodeFormId = (encodedFormId) => {
    try {
      if (!encodedFormId) return null;
      const bytes = base64.toByteArray(encodedFormId);
      return new TextDecoder().decode(bytes);
    } catch (error) {
      console.error("Error decoding formId:", error);
      return null;
    }
  };
  const formId = decodeFormId(encodedFormId);

  const fetchData = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/Branch/GetBranch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });
      if (response.ok) {
        const responseData = await response.json();
        setData(responseData || []);
        setLoadingBarProgress(100);
      } else {
        console.error("Failed to fetch branches:", response.status);
        // alert("Invalid account name");
        setSnackbarMessage('Invalid account name!');
        setOpenSnackbar(true);
        setSnackbarSeverity("warning")
        setLoadingBarProgress(0);
      }
    } catch (error) {
      console.error("API request error:", error);
      // alert("An error occurred. Please try again later.");
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error")
      setLoadingBarProgress(0);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPermissions = async () => {
    if (!formId) {
      console.error("Invalid formId:", encodedFormId);
      // alert("Invalid form ID. Please check the URL.");
      setSnackbarMessage('Invalid form ID. Please check the URL.');
      setOpenSnackbar(true);
      setSnackbarSeverity("warning")
      setIsLoading(false);
      return;
    }

    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          formId,
        }),
      });

      if (response.ok) {
        const responseData = await response.json();
        if (responseData && responseData.length > 0) {
          setAuthorization(responseData);
          setCanEdit(responseData[0].uModify === 1);
          setLoadingBarProgress(100);
        } else {
          console.error("No permission data received");
          // alert("No permission data available.");
          setSnackbarMessage('No permission data available.');
          setOpenSnackbar(true);
          setSnackbarSeverity("warning")
          setLoadingBarProgress(0);
        }
      } else {
        console.error("Authorization failed:", response.status);
        // alert("Invalid authorization response");
        setSnackbarMessage('Invalid authorization response');
        setOpenSnackbar(true);
        setSnackbarSeverity("error")
        setLoadingBarProgress(0);
      }
    } catch (error) {
      console.error("Permission API request error:", error);
      // alert("An error occurred while checking permissions.");
      setSnackbarMessage('An error occurred while checking permissions.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error")
      setLoadingBarProgress(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditBranch = (branchId) => {
    if (canEdit) {
      navigate(`/editbranch/${branchId}`);
    } else {
      // alert("You are not authorized to edit branch details.");
      setSnackbarMessage('You are not authorized to edit branch details.');
      setOpenSnackbar(true);
      setSnackbarSeverity("warning")
    }
  };

  useEffect(() => {
    setIsLoading(true);
    Promise.all([fetchData(), fetchPermissions()]).then(() => {
      setIsLoading(false);
    });
  }, []);

  const filteredData = data.filter((branch) =>
    branch.branchName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Container>
      <AppBar position="static" style={{ backgroundColor: "#0B1F3D" }}>
        <Toolbar>
          <Typography variant="h4" component="div" style={{ fontWeight: 'bold' }}>
            Branch
          </Typography>
        </Toolbar>
      </AppBar>
      <Paper
        elevation={3}
        style={{ padding: 16, width: "100%", margin: "auto", marginTop: 6 }}
      >
        <LoadingBar
          progress={loadingBarProgress}
          color="rgb(152, 106, 182)"
          height={5}
        />
        {isLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="50vh"
          >
            <CircularProgress size={60} />
          </Box>
        ) : (
          <>
            <div
              className="form-group"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "1rem",
                marginBottom: "1rem",
              }}
            >
              <TextField
                variant="outlined"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  style: { padding: "0px 8px" },
                }}
                sx={{
                  width: 300,
                  backgroundColor: "#f9f9f9",
                }}
              />
              <Tooltip title="Add Branch" arrow>
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={()=>{navigate('/addbranch')}}
                    sx={{ minWidth: 120 }}
                  >
                    Add Branch
                  </Button>
                </span>
              </Tooltip>
            </div>

            <TableContainer component={Paper} mt={3}>
              <Table
                sx={{
                  minWidth: 650,
                  border: '1px solid #ccc',
                  '& .MuiTableCell-root': {
                    borderRight: '1px solid #ccc', // vertical line
                    fontSize: '1rem', // increased font size
                  },
                  '& .MuiTableHead-root .MuiTableCell-root': {
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell><b>Organization</b></TableCell>
                    <TableCell><b>Branch Name</b></TableCell>
                    <TableCell><b>Email-Id</b></TableCell>
                    <TableCell><b>Mobile No</b></TableCell>
                    <TableCell><b>Address</b></TableCell>
                    <TableCell align="center"><b>Actions</b></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData.length > 0 ? (
                    filteredData.map((item) => (
                      <TableRow key={item.branchId}>
                        <TableCell>{item.organisationName || "N/A"}</TableCell>
                        <TableCell>{item.branchName || "N/A"}</TableCell>
                        <TableCell>{item.emailID || "N/A"}</TableCell>
                        <TableCell>{item.mobileNo || "N/A"}</TableCell>
                        <TableCell>{item.branchAddress || "N/A"}</TableCell>
                        <TableCell align="center">
                          <Tooltip
                            title={canEdit ? "" : "Not authorized to edit"}
                            arrow
                          >
                            <span>
                              <Button
                                variant="contained"
                                color="warning"
                                onClick={() => handleEditBranch(item.branchId)}
                                disabled={!canEdit}
                                sx={{ minWidth: 80 }}
                              >
                                Edit
                              </Button>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6}>No branches available</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

          </>
        )}
      </Paper>
      <Snackbar
  open={openSnackbar}
  autoHideDuration={4000}
  onClose={() => setOpenSnackbar(false)}
  anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
>
  <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
    {snackbarMessage}
  </Alert>
</Snackbar>

    </Container>
  );
};

export default Branch;
